import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { 
  Card, 
  CardBody, 
  Button, 
  OutlineButton, 
  Badge, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableHeader,
  FlexContainer,
  Input,
  Select,
  FormGroup,
  Label,
  TextArea,
  Grid
} from '../../styles/StyledComponents';
import { 
  FaPlus, 
  FaSearch, 
  FaFileExcel, 
  FaFilePdf, 
  FaEye, 
  FaEdit, 
  FaFilter, 
  FaSortAmountDown, 
  FaSortAmountUp, 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaUserTie,
  FaChartLine,
  FaTimes,
  FaInfoCircle,
  FaCheckCircle,
  FaPause,
  FaProjectDiagram,
  FaArrowUp,
  FaArrowDown,
  FaDownload,
  FaLayerGroup,
  FaMoneyBillWave,
  FaReceipt,
  FaCalculator,
  FaClipboardList,
  FaExclamationTriangle,
  FaCheckDouble,
  FaClock,
  FaBuilding,
  FaTools,
  FaTruck,
  FaHandshake,
  FaCog
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';
import { useAuth } from '../../context/AuthContext';
import { Project, ProjectStatus, ProjectType } from '../../types';
import apiService from '../../services/api';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const PageContainer = styled.div`
  margin-bottom: 2rem;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(20, 19, 64, 0.08);
  border: 1px solid #f1f3f4;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(246, 139, 33, 0.02) 0%, rgba(20, 19, 64, 0.01) 100%);
    border-radius: 16px;
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #141340;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  z-index: 1;
  
  .title-icon {
    background: linear-gradient(135deg, #F68B21 0%, #141340 100%);
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
    box-shadow: 0 8px 24px rgba(20, 19, 64, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const ActionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(246, 139, 33, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(20, 19, 64, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ProjectInfoCard = styled(Card)`
  margin-bottom: 2rem;
  border: 2px solid #F68B21;
  background: linear-gradient(135deg, #fff9f0 0%, #ffffff 100%);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(246, 139, 33, 0.15);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(20, 19, 64, 0.2);
  }
  
  .project-header {
    background: linear-gradient(135deg, #F68B21 0%, #141340 100%);
    color: white;
    padding: 2rem;
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 200px;
      height: 200px;
      background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
      transform: translate(50%, -50%);
    }
    
    h3 {
      margin: 0;
      font-size: 1.8rem;
      font-weight: 700;
      position: relative;
      z-index: 1;
    }
    
    .project-meta {
      margin-top: 0.75rem;
      font-size: 1rem;
      opacity: 0.95;
      position: relative;
      z-index: 1;
      display: flex;
      align-items: center;
      gap: 1rem;
      flex-wrap: wrap;
    }
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div<{ color?: string; delay?: number }>`
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  padding: 1rem;
  text-align: center;
  animation: ${fadeInUp} 0.6s ease-out ${props => props.delay || 0}s both;
  transition: all 0.3s ease;
  border: 1px solid rgba(20, 19, 64, 0.05);
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(20, 19, 64, 0.15);
    border-color: rgba(20, 19, 64, 0.1);
  }
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  color: #141340;
  margin-bottom: 0.25rem;
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const QuickActionsCard = styled(Card)`
  margin-bottom: 2rem;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  }
`;

const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
`;

const QuickActionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 16px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  justify-content: flex-start;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 12px 32px rgba(246, 139, 33, 0.3);
    
    &::before {
      opacity: 1;
    }
  }
  
  &:active {
    transform: translateY(-2px) scale(1.01);
  }
`;

const ExpenseCard = styled(Card)`
  margin-bottom: 1.5rem;
  border-left: 4px solid #F68B21;
`;

const ExpenseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  h4 {
    margin: 0;
    color: #141340;
    font-weight: 600;
  }
  
  .amount {
    font-size: 1.2rem;
    font-weight: 700;
    color: #F68B21;
  }
`;

const ExpenseMeta = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
`;

const StatusBadge = styled(Badge)`
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const TableCard = styled(Card)`
  margin-bottom: 2rem;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  }
`;

const TableActionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 1.5rem 0;
  border-bottom: 2px solid #f8f9fa;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const ResultCount = styled.div`
  font-size: 1rem;
  color: #495057;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const TableActionButton = styled(OutlineButton)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 12px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #6c757d;
  
  .empty-icon {
    font-size: 3rem;
    color: #dee2e6;
    margin-bottom: 1rem;
  }
  
  .empty-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.5rem;
  }
  
  .empty-description {
    font-size: 0.9rem;
    color: #6c757d;
  }
`;

const CashierDashboard: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [assignedProject, setAssignedProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [showExpenseForm, setShowExpenseForm] = useState(false);

  useEffect(() => {
    const loadCashierData = async () => {
      try {
        setLoading(true);
        
        // Get projects assigned to the current cashier
        const assignedProjectsResponse = await apiService.getAssignedProjects();
        console.log('Assigned projects response:', assignedProjectsResponse);
        const assignedProjects = assignedProjectsResponse.data || [];
        
        if (assignedProjects.length > 0) {
          // For now, use the first assigned project
          // In the future, you might want to show multiple projects or let user select
          const project = assignedProjects[0];
          console.log('Using assigned project:', project);
          setAssignedProject(project);
          
          // Load expenses for the assigned project
          const expensesResponse = await apiService.getExpenses({ 
            projectId: project._id || project.id 
          });
          console.log('Expenses response:', expensesResponse);
          setExpenses(expensesResponse.data || []);
        } else {
          console.log('No projects assigned to cashier');
          setAssignedProject(null);
          setExpenses([]);
        }
      } catch (error) {
        console.error('Error loading cashier data:', error);
        setAssignedProject(null);
        setExpenses([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (user) {
      loadCashierData();
    }
  }, [user, location.pathname]); // Refresh when route changes (e.g., returning from expense form)

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        fontSize: '1.1rem',
        color: '#6c757d'
      }}>
        Loading your assigned project...
      </div>
    );
  }

  if (!assignedProject) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle>
            <div className="title-icon">
              <IconWrapper icon={FaMoneyBillWave} />
            </div>
            Cashier Dashboard
          </PageTitle>
        </PageHeader>
        
        <Card>
          <CardBody style={{ textAlign: 'center', padding: '3rem' }}>
            <div style={{ fontSize: '3rem', color: '#dee2e6', marginBottom: '1rem' }}>
              <IconWrapper icon={FaExclamationTriangle} />
            </div>
            <h3 style={{ color: '#495057', marginBottom: '1rem' }}>No Project Assigned</h3>
            <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
              You are not currently assigned to any project. Please contact your supervisor to get assigned to a project.
            </p>
          </CardBody>
        </Card>
      </PageContainer>
    );
  }

  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Pending': return 'warning';
      case 'Rejected': return 'danger';
      default: return 'info';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Materials': return FaBuilding;
      case 'Labor': return FaTools;
      case 'Equipment': return FaTruck;
      case 'Sub-Contracts': return FaHandshake;
      case 'Others': return FaCog;
      default: return FaMoneyBillWave;
    }
  };

  const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);
  const approvedExpenses = expenses.filter(exp => exp.status === 'Approved').reduce((sum, exp) => sum + exp.amount, 0);
  const pendingExpenses = expenses.filter(exp => exp.status === 'Pending').reduce((sum, exp) => sum + exp.amount, 0);
  const remainingBudget = assignedProject.budget.total - totalExpenses;

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>
          <div className="title-icon">
            <IconWrapper icon={FaMoneyBillWave} />
          </div>
          Dashboard
        </PageTitle>
        <FlexContainer gap="1rem">
          <ActionButton as={Link} to="/cashier/expense/new">
            <IconWrapper icon={FaPlus} />
            Record Expense
          </ActionButton>
          <OutlineButton as={Link} to="/cashier/reports">
            <IconWrapper icon={FaFilePdf} />
            Generate Report
          </OutlineButton>
        </FlexContainer>
      </PageHeader>

      <ProjectInfoCard>
        <div className="project-header">
          <h3>{assignedProject.name}</h3>
          <div className="project-meta">
            <IconWrapper icon={FaMapMarkerAlt} /> {assignedProject.location} • 
            {/* <IconWrapper icon={FaUserTie} /> {assignedProject.assignedStaff?.find((staff: any) => staff.role === 'Project Manager')?.name} • */}
            <IconWrapper icon={FaCalendarAlt} /> {assignedProject.status}
          </div>
        </div>
        <CardBody>
          <Grid columns="1fr 1fr" gap="2rem">
            <div>
              <h4 style={{ marginBottom: '1rem', color: '#141340' }}>Project Details</h4>
              <div style={{ lineHeight: '1.8' }}>
                <div><strong>Project ID:</strong> {assignedProject._id || assignedProject.id}</div>
                <div><strong>Type:</strong> {assignedProject.type}</div>
                <div><strong>Budget:</strong> {formatCurrency(assignedProject.budget.total, assignedProject.budget.currency)}</div>
                                 <div><strong>Start Date:</strong> {assignedProject.startDate ? new Date(assignedProject.startDate).toLocaleDateString() : 'Not set'}</div>
                 <div><strong>End Date:</strong> {assignedProject.endDate ? new Date(assignedProject.endDate).toLocaleDateString() : 'Not set'}</div>
              </div>
            </div>
            <div>
              <h4 style={{ marginBottom: '1rem', color: '#141340' }}>Budget Breakdown</h4>
              <div style={{ lineHeight: '1.8' }}>
                <div><strong>Materials:</strong> {formatCurrency(assignedProject.budget.materials, assignedProject.budget.currency)}</div>
                <div><strong>Labor:</strong> {formatCurrency(assignedProject.budget.labor, assignedProject.budget.currency)}</div>
                <div><strong>Equipment:</strong> {formatCurrency(assignedProject.budget.equipment, assignedProject.budget.currency)}</div>
                <div><strong>Sub-Contracts:</strong> {formatCurrency(assignedProject.budget.subContracts, assignedProject.budget.currency)}</div>
                <div><strong>Others:</strong> {formatCurrency(assignedProject.budget.others, assignedProject.budget.currency)}</div>
              </div>
            </div>
          </Grid>
        </CardBody>
      </ProjectInfoCard>

      <StatsContainer>
        <StatCard color="#F68B21" delay={0.1}>
          <StatContent>
            <StatValue>{formatCurrency(totalExpenses)}</StatValue>
            <StatLabel>Total Expenses</StatLabel>
          </StatContent>
        </StatCard>
        
        <StatCard color="#28a745" delay={0.2}>
          <StatContent>
            <StatValue>{formatCurrency(approvedExpenses)}</StatValue>
            <StatLabel>Approved Expenses</StatLabel>
          </StatContent>
        </StatCard>
        
        <StatCard color="#ffc107" delay={0.3}>
          <StatContent>
            <StatValue>{formatCurrency(pendingExpenses)}</StatValue>
            <StatLabel>Pending Expenses</StatLabel>
          </StatContent>
        </StatCard>
        
        <StatCard color="#17a2b8" delay={0.4}>
          <StatContent>
            <StatValue>{formatCurrency(remainingBudget)}</StatValue>
            <StatLabel>Remaining Budget</StatLabel>
          </StatContent>
        </StatCard>
      </StatsContainer>

      <QuickActionsCard>
        <CardBody>
          <h3 style={{ marginBottom: '1rem', color: '#141340' }}>
            <IconWrapper icon={FaClipboardList} />
            Quick Actions
          </h3>
          <QuickActionsGrid>
            <QuickActionButton as={Link} to="/cashier/expense/new">
              <IconWrapper icon={FaPlus} />
              Record New Expense
            </QuickActionButton>
            <QuickActionButton as={Link} to="/cashier/expenses">
              <IconWrapper icon={FaReceipt} />
              View All Expenses
            </QuickActionButton>
            <QuickActionButton as={Link} to="/cashier/reports">
              <IconWrapper icon={FaFilePdf} />
              Generate Report
            </QuickActionButton>
            <QuickActionButton as={Link} to={`/projects/${assignedProject._id || assignedProject.id}`}>
              <IconWrapper icon={FaEye} />
              View Project Details
            </QuickActionButton>
          </QuickActionsGrid>
        </CardBody>
      </QuickActionsCard>

      <TableCard>
        <CardBody>
          <TableActionsRow>
            <ResultCount>
              <IconWrapper icon={FaReceipt} />
              Recent Expenses ({expenses.length})
            </ResultCount>
            <FlexContainer gap="0.5rem">
              <OutlineButton as={Link} to="/cashier/expenses">
                View All
              </OutlineButton>
              <OutlineButton as={Link} to="/cashier/expense/new">
                Add Expense
              </OutlineButton>
            </FlexContainer>
          </TableActionsRow>
          
          <TableContainer>
            <Table>
              <TableHead>
                <tr>
                  <TableHeader>Description</TableHeader>
                  <TableHeader>Category</TableHeader>
                  <TableHeader>Amount</TableHeader>
                  <TableHeader>Date</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Actions</TableHeader>
                </tr>
              </TableHead>
              <tbody>
                {expenses.length > 0 ? (
                  expenses.map((expense) => (
                    <TableRow key={expense._id || expense.id}>
                      <TableCell>
                        <div style={{ fontWeight: '500', color: '#141340' }}>
                          {expense.description}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                          {expense.supplier}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <IconWrapper icon={getCategoryIcon(expense.category)} />
                          {expense.category}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div style={{ fontWeight: '600', color: '#F68B21' }}>
                          {formatCurrency(expense.amount)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                          {new Date(expense.date).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge variant={getStatusVariant(expense.status)}>
                          {expense.status}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <FlexContainer gap="0.5rem">
                          <Link to={`/cashier/expense/${expense._id || expense.id}`}>
                            <TableActionButton>
                              <IconWrapper icon={FaEye} />
                              View
                            </TableActionButton>
                          </Link>
                          <Link to={`/cashier/expense/edit/${expense._id || expense.id}`}>
                            <TableActionButton>
                              <IconWrapper icon={FaEdit} />
                              Edit
                            </TableActionButton>
                          </Link>
                        </FlexContainer>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <EmptyState>
                        <div className="empty-icon">
                          <IconWrapper icon={FaReceipt} />
                        </div>
                        <div className="empty-title">No expenses recorded yet</div>
                        <div className="empty-description">
                          Start recording expenses for this project to track spending.
                        </div>
                      </EmptyState>
                    </TableCell>
                  </TableRow>
                )}
              </tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </TableCard>
      
      <div style={{ 
        textAlign: 'right', 
        padding: '2rem 1rem 1rem', 
        color: '#6c757d', 
        fontSize: '0.8rem' 
      }}>
        Developed by Sandfish
      </div>
    </PageContainer>
  );
};

export default CashierDashboard; 